import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DocumentService } from 'src/app/core/services/document.service';
import { TemplateService } from 'src/app/core/services/template.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';

@Component({
  selector: 'app-add-template',
  templateUrl: './add-template.component.html',
  styleUrl: './add-template.component.scss'
})
export class AddTemplateComponent {
  @Output() back = new EventEmitter<any>();
  @Input() template: any = {};

  templateForm!: FormGroup;
  signData: any = {};
  pdfPreviewUrl: any
  selectedFile: File | null = null;
  isShowDoc: boolean = false;
  signerList: any = [{ name: 'Signer 1' }];
  allSignersPositions: any = [];
  categories = ['Fintech', 'Real Estate', 'Healthcare', 'Legal'];
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  isEditTemp: boolean = false;
  templateId: string = '';
  loading: boolean = false;
  validationSummary: string[] = [];


  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private templateService: TemplateService,
    private documentService: DocumentService,
    private confirmModalService: ConfirmModalService
  ) { }

  async ngOnInit() {
    this.templateForm = this.fb.group({
      name: ['', Validators.required],
      category: ['', Validators.required],
      description: [''],
    });

    if (Object.keys(this.template).length > 0) {
      console.log(this.template)
      this.isEditTemp = true;
      this.templateId = this.template?._id;
      this.templateForm.patchValue({
        name: this.template?.name,
        category: this.template?.category,
        description: this.template?.description,
      });

      this.allSignersPositions = this.template?.allSignersPositions?.slice() || [];
      this.signerList = this.template?.allSignersPositions?.map((s: any) => ({
        ...s,
        signatureDetail: s?.signatureData ?? s?.signatureDetail ?? null
      })) || [];

      this.documentService.setSigners = this.signerList;
    }

    if (this.template?.fileUrl) {
      try {
        this.selectedFile = await this.convertUrlToFile(this.template.fileUrl);
      } catch (error) {
        console.error('Failed to load template file for edit:', error);
        this.selectedFile = null;
      }
    }

    this.documentService.setSigners = this.signerList;
  }

  async loadPdf(fileUrl: string) {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      // 👉 This creates blob URL like: blob:http://localhost:4200/xxxx
      const file = URL.createObjectURL(blob);

      return file // ✅ blob URL

    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  }

  get isFileAvailable(): boolean {
    return !!(this.selectedFile || this.template?.fileUrl);
  }

  async convertUrlToFile(url: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();

    const fileName = url.split('/').pop() || 'document.pdf';

    return new File([blob], fileName, {
      type: blob.type || 'application/pdf'
    });
  }

  async crateTemplate() {
    if (this.loading) return;

    this.validationSummary = [];
    this.templateForm.markAllAsTouched();

    const validationMessages = new Set<string>();

    if (this.templateForm.get('name')?.hasError('required')) {
      validationMessages.add('Template name is required.');
    }

    if (this.templateForm.get('category')?.hasError('required')) {
      validationMessages.add('Category is required.');
    }

    if (!this.isFileAvailable) {
      validationMessages.add('Document is required.');
    }

    if (!this.signerList.length) {
      validationMessages.add('At least one signer is required.');
    }

    this.signerList.forEach((signer: any, index: number) => {
      const signatureData =
        this.allSignersPositions[index]?.signatureData ??
        this.allSignersPositions[index]?.signatureDetail;
      const hasPosition = Array.isArray(signatureData) &&
        signatureData.some((page: any) =>
          Array.isArray(page?.coordinates) && page.coordinates.length > 0
        );

      if (!hasPosition) {
        const name = String(signer?.name || '').trim();
        const email = String(signer?.email || '').trim();
        const signerIdentity = name && email
          ? `${name} (${email})`
          : name || email || `Signer ${index + 1}`;

        validationMessages.add(`Signature position is required for ${signerIdentity}.`);
      }
    });

    this.validationSummary = Array.from(validationMessages);

    if (this.validationSummary.length) {
      return;
    }

    if (this.isEditTemp) {
      this.confirmModalService.open(
        'Confirm Template Update',
        'Are you sure you want to update this document template?',
        () => {
          this.submitTemplateData();
        },
        'Update Template',
        'Cancel'
      );
      return;
    }

    this.confirmModalService.open(
      'Confirm Template Submission',
      'Are you sure you want to create this document template?',
      () => {
        this.submitTemplateData();
      },
      'Create Template',
      'Cancel'
    );
  }
  async submitTemplateData() {
    if (this.loading) return;

    this.loading = true;

    try {
      let file = this.selectedFile;

      if (Object.keys(this.template).length > 0 && this.template?.fileUrl && !file) {
        file = await this.convertUrlToFile(this.template.fileUrl);
      }

      const templateData = {
        ...this.templateForm.value,
        allSignersPositions: this.allSignersPositions.length
          ? this.allSignersPositions
          : this.template?.allSignersPositions,
        file: this.selectedFile ? this.selectedFile : file,
      };

      this.templateService.createTemplate(templateData, this.templateId).subscribe(
        (res: any) => {
          this.loading = false;
          this.navigateBack();
          this.isEditTemp = false;
          this.templateForm.reset();
          this.documentService.setSigners = [];

          this.toastService.showToaster(
            this.templateId ? "Template Updated" : "Template Created",
            this.templateId
              ? "The template has been updated successfully."
              : "The template has been created successfully.",
            "success"
          );

          this.back.emit({
            addTemp: true
          });
        },
        () => {
          this.loading = false;
        }
      );
    } catch (error) {
      this.loading = false;
      this.toastService.showToaster(
        "Error",
        "Something went wrong. Please try again.",
        "error"
      );
    }
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0
      ? input.files[0]
      : null;

    if (file) {
      const maxSizeInBytes = 1 * 1024 * 1024; // 1MB

      if (file.type !== 'application/pdf') {
        this.toastService.showToaster(
          "Invalid File",
          "Only PDF files are allowed and the max file size is 1MB.",
          "error"
        );
        input.value = '';
        this.selectedFile = null;
        this.pdfPreviewUrl = null;
        return;
      }

      if (file.size > maxSizeInBytes) {
        this.toastService.showToaster(
          "File Too Large",
          "Only PDF files are allowed and the max file size is 1MB.",
          "error"
        );
        input.value = '';
        this.selectedFile = null;
        this.pdfPreviewUrl = null;
        return;
      }
    }

    this.selectedFile = file;

    if (this.selectedFile) {
      this.pdfPreviewUrl = URL.createObjectURL(this.selectedFile);
    } else {
      this.pdfPreviewUrl = null;
    }
  }

  navigateBack() {
    this.isEditTemp = false;
    this.back.emit({
      edit: this.isEditTemp
    });
  }

  close(event: any) {
    this.isShowDoc = false;

    if (event?.signatureData && event?.signerIndex !== undefined) {
      const signerIndex = Number(event.signerIndex);
      this.allSignersPositions[signerIndex] = {
        ...(this.allSignersPositions[signerIndex] || {}),
        signatureData: event.signatureData
      };

      const updatedSigners = this.documentService.getSigners.map((signer, index) => {
        if (index === signerIndex) {
          return {
            ...signer,
            signatureDetail: event.signatureData
          };
        }
        return signer;
      });

      this.documentService.setSigners = updatedSigners;
    }

    console.log(event);
    console.log(this.allSignersPositions);
  }

  addSigner() {
    if (this.signerList.length < 5) {
      this.signerList.push({ name: `Signer ${this.signerList.length + 1}` });
      this.documentService.setSigners = this.signerList;
    }
  }

  removeSigner(index: number) {
    this.isShowDoc = false;
    this.signerList.splice(index, 1);
    this.allSignersPositions.splice(index, 1);
    this.documentService.setSigners = this.signerList;
  }

  async signDoc(index: any) {
    if (this.template?.fileUrl) {
      this.pdfPreviewUrl = await this.loadPdf(this.template.fileUrl);
    }
    const recipient: any = this.documentService.getSigners[index]
    console.log(recipient)
    this.signData = {
      pdfUrl: this.pdfPreviewUrl,
      name: `Signer ${index + 1}`,
      signatureDetail: recipient?.signatureData,
      signerIndex: index,
      editable: true
    }
    this.isShowDoc = true;
  }

}
